import type { FlowRunNode } from '../types/index.js';

/**
 * Latest run-node per flow node id, selected by the numeric `iteration`
 * column, optionally restricted to rows matching `predicate`. Run-node reads
 * carry NO ordering guarantee (see the FlowStorageAdapter contract in
 * interfaces.ts), so "latest" is an explicit max-by-iteration, never a
 * positional pick. Iterations are unique per (runId, nodeId) — run-node ids
 * embed the iteration — so ties cannot occur; on a hypothetical tie the
 * first row seen wins. Never derive "latest" by sorting run-node id strings:
 * ':10' < ':2'.
 */
export function latestByNodeId(
  runNodes: ReadonlyArray<FlowRunNode>,
  predicate?: (rn: FlowRunNode) => boolean,
): Map<string, FlowRunNode> {
  const latest = new Map<string, FlowRunNode>();
  for (const rn of runNodes) {
    if (predicate && !predicate(rn)) continue;
    const existing = latest.get(rn.nodeId);
    if (!existing || rn.iteration > existing.iteration) latest.set(rn.nodeId, rn);
  }
  return latest;
}

/**
 * Latest completed run-node per flow node id — {@link latestByNodeId}
 * restricted to `status === 'completed'`.
 */
export function latestCompletedByNodeId(
  runNodes: ReadonlyArray<FlowRunNode>,
): Map<string, FlowRunNode> {
  return latestByNodeId(runNodes, (rn) => rn.status === 'completed');
}
